import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { supabase } from '../lib/supabase';

export type UserPlan = 'free' | 'premium';

interface PlanContextValue {
  /** Stored plan in DB (free / premium) */
  userPlan: UserPlan;
  /** Whether we are still loading plan from Supabase */
  isPlanLoading: boolean;
  /** Lifetime premium via founder status */
  isFounder: boolean;
  /** Use for all feature gating: premium OR founder */
  hasPremiumAccess: boolean;
  analysisCount: number;
  refetchPlan: () => Promise<void>;
}

const PlanContext = createContext<PlanContextValue>({
  userPlan: 'free',
  isPlanLoading: true,
  isFounder: false,
  hasPremiumAccess: false,
  analysisCount: 0,
  refetchPlan: async () => {},
});

// eslint-disable-next-line react-refresh/only-export-components
export function usePlan() {
  const ctx = useContext(PlanContext);
  if (!ctx) throw new Error('usePlan must be used within PlanProvider');
  return ctx;
}

interface PlanProviderProps {
  children: ReactNode;
}

export function PlanProvider({ children }: PlanProviderProps) {
  const [userPlan, setUserPlan] = useState<UserPlan>('free');
  const [isFounder, setIsFounder] = useState(false);
  const [analysisCount, setAnalysisCount] = useState(0);
  const [isPlanLoading, setIsPlanLoading] = useState(true);

  // Beta posture: no paywall while pricing is deferred. Every signed-in user
  // gets premium-gated features. When pricing turns on, swap back to:
  // `userPlan === 'premium' || isFounder`.
  const hasPremiumAccess = true;

  const refetchPlan = useCallback(async () => {
    setIsPlanLoading(true);
    const { data: auth } = await supabase.auth.getUser();
    const user = auth.user;
    if (!user) {
      setUserPlan('free');
      setIsFounder(false);
      setAnalysisCount(0);
      setIsPlanLoading(false);
      return;
    }
    const [profileRes, countRes] = await Promise.all([
      supabase
        .from('profiles')
        .select('plan, is_founder')
        .eq('id', user.id)
        .maybeSingle(),
      supabase.from('analyses').select('*', { count: 'exact', head: true }).eq('user_id', user.id),
    ]);
    const row = profileRes.data as { plan?: string; is_founder?: boolean } | null;
    const plan = row?.plan;
    const founder = !!row?.is_founder;
    setIsFounder(founder);
    setUserPlan(founder ? 'premium' : plan === 'premium' ? 'premium' : 'free');
    setAnalysisCount(countRes.count ?? 0);
    setIsPlanLoading(false);
  }, []);

  // Wait for Supabase auth to settle before checking plan.
  // This prevents a race where session isn't restored yet → user reads as null → plan = free → redirect to /upgrade.
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) {
        void refetchPlan();
      } else {
        setUserPlan('free');
        setIsFounder(false);
        setAnalysisCount(0);
        setIsPlanLoading(false);
      }
    });
    // Also do an immediate check in case session is already present
    void refetchPlan();
    return () => { subscription.unsubscribe(); };
  }, [refetchPlan]);

  return (
    <PlanContext.Provider
      value={{
        userPlan,
        isPlanLoading,
        isFounder,
        hasPremiumAccess,
        analysisCount,
        refetchPlan,
      }}
    >
      {children}
    </PlanContext.Provider>
  );
}
